import React from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Tooltip, TooltipTrigger, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipContent,
} from '../../../components/tooltip';
import { DismissableBadge } from '../../../components/filters/badges/dismissable-badge';
import { FilterBadgeProps, FilterBadgeDataItem } from './types';

// Re-export FilterBadgeDataItem for backward compatibility
export type { FilterBadgeDataItem } from './types';

/**
 * Generic filter badge component that can be used for any type of filter data
 * Provides consistent styling and behavior across all filter badges
 */
const GenericFilterBadge = React.forwardRef<
  HTMLDivElement,
  FilterBadgeProps & {
    label: string;
    tooltipTitle: string;
    tooltipKey: string;
    tooltipValue?: string;
    ariaLabel: string;
    dismissAriaLabel: string;
    srOnlyText?: string;
    getDisplayText:(item: FilterBadgeDataItem) => string;
    getTooltipValue?: (data: FilterBadgeDataItem[]) => string;
    showCount?: boolean;
    badgeKey?: string;
  }
>(
  (
    {
      children: _children,
      className: _className,
      data,
      onDismiss,
      label,
      tooltipTitle,
      tooltipKey,
      tooltipValue,
      ariaLabel,
      dismissAriaLabel,
      srOnlyText,
      getDisplayText,
      getTooltipValue,
      showCount = true,
      badgeKey,
      ...props
    },
    ref,
  ) => {
    // Do not display a badge when no data is selected
    if (data.length === 0) {
      return null;
    }

    const displayText = getDisplayText(data[0]);
    const countText = showCount && data.length > 1 ? `+${data.length - 1}` : null;
    const finalTooltipValue = getTooltipValue
      ? getTooltipValue(data)
      : tooltipValue || data.map(getDisplayText).join(', ');
    const finalSrOnlyText = srOnlyText || `Remove ${label.toLowerCase()} filter for ${displayText}`;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <DismissableBadge
                ref={ref}
                key={badgeKey || data[0].id}
                variant="secondary"
                className="mr-2 mb-2"
                aria-label={ariaLabel}
                role="button"
                data-dd-action-name="open-ads-filters"
                {...props}
              >
                {label}:<span className="capitalize ml-1">{displayText}</span>
                {countText && <span className="ml-1">{countText}</span>}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(e);
                  }}
                  aria-label={dismissAriaLabel}
                  className="flex ml-1 p-1 focus:outline-none focus:ring"
                  data-dd-action-name="remove-ads-filters"
                >
                  <Cross2Icon className="h-3 w-3" />
                </button>
                <span className="sr-only">{finalSrOnlyText}</span>
              </DismissableBadge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <TooltipHeader>
              <TooltipTitle>{tooltipTitle}</TooltipTitle>
            </TooltipHeader>
            <TooltipBody>
              <TooltipItem>
                <TooltipItemKey>{tooltipKey}</TooltipItemKey>
                <TooltipItemValue>{finalTooltipValue}</TooltipItemValue>
              </TooltipItem>
            </TooltipBody>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

GenericFilterBadge.displayName = 'GenericFilterBadge';

export { GenericFilterBadge };
